import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import PDPProtectionPlanSelector from 'components/consumer/PDP/PDPProtectionPlanSelector';
import useProductEFGInternalStatus from 'data-hooks/useProductEFGInternalStatus';

import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import ReRenderOnMount from 'components/consumer/ReRenderOnMount';
import PDPNameSection from '../PDPNameSection';
import PDPMobileStudioPhotoGallery from '../PDPMobileStudioPhotoGallery';
import PDPShopTabsBar from '../PDPShopTabsBar';
import PDPAvailableInSizes from '../PDPAvailableInSizes';
import PDPOnDisplayInShowroom from '../PDPOnDisplayInShowroom';
import PDPMobileConfigurationPhotoGallery from '../PDPMobileConfigurationPhotoGallery';
import PDPChooseYourOptions from '../PDPChooseYourOptions';
import PDPUpSellProduct from '../PDPUpSellProduct';
import PDPCompleteOrder from '../PDPCompleteOrder';
import PDPAddToBagSection from '../PDPAddToBagSection';
import PDPKeySellingPoints from '../PDPKeySellingPoints';
import PDPSellingAttributes from '../PDPSellingAttributes';
import PDPProductDescription from '../PDPProductDescription';
import PDPDimensionsAndDetails from '../PDPDimensionsAndDetails';
import PDPCustomerPhotos from '../PDPCustomerPhotos';
import PDPBreadCrumbs from '../PDPBreadCrumbs';
import PDPPress from '../PDPPress';
import PDPAlertBar from '../PDPAlertBar';
import PDPDetailsAndProductCareLink from '../PDPDetailsAndProductCareLink';
import PDPAlertsSection from '../PDPAlertsSection';
import PDPDiscontinuedMessage from '../PDPDiscontinuedMessage';
import usePDPOptions from '../state/context/usePDPOptions';
import PDPMobilePlaceholder from '../PDPMobilePlaceholder';

const PDPCollectionSlider = loadable(() => import('../PDPCollectionSlider'));

const ModularAddProducts = loadable(() =>
  import('components/consumer/modular-configurator/ModularAddProducts')
);

const ModularAddProductsMarxent = loadable(() =>
  import('components/consumer/modular-configurator/ModularAddProducts/marxent')
);

const PDPMobileInner = ({
  slug,
  isOutOfStock = false,
  showOutOfStockNotification = {},
  pdpIsModularBYOProduct = false,
  isEditOrder = false,
  showDiscontinuedNotification = false,
  discontinuedCategoryMsg = null,
}) => {
  const { isEFGInternal = false } = useProductEFGInternalStatus();
  const { data: optionData } = usePDPOptions();
  const [efgInternalKey, setEfgInternalKey] = useState('0');
  const [displayPressComp, setDisplayPressComp] = useState(false);
  const [marxentSelectedFabric, setMarxentSelectedFabric] = useState(null);
  const [hideMarxent, setHideMarxent] = useState(true);
  const [unavailableFabric, setUnavailableFabric] = useState(false);

  const galleryRef = useRef(null);

  useEffect(() => {
    if (isEFGInternal && !isNewConsumerApp) {
      setEfgInternalKey('1');
    }
  }, [isEFGInternal]);

  const mobileMarxentFeatureFlag = usePostHogFeatureFlag(
    'marxent-mobile-integration'
  );

  // This is to bring the Marxent configurator into view when a product is added
  const scrollToGallery = useCallback(() => {
    if (galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="PDPMobile lg:hidden" data-xc="PDPMobile">
      <div className="mb-4">
        <PDPBreadCrumbs />
      </div>

      <PDPNameSection isEditOrder={isEditOrder} />

      <FixedWidthContainer>
        {!isEditOrder ? (
          <PDPAvailableInSizes />
        ) : (
          <div className="PDP__PDPAvailableInSizes-filler" />
        )}

        <PDPOnDisplayInShowroom />
      </FixedWidthContainer>

      <div>
        <PDPMobileStudioPhotoGallery
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
        />
      </div>

      <FixedWidthContainer>
        <PDPShopTabsBar isEditOrder={isEditOrder} key={efgInternalKey} />
      </FixedWidthContainer>

      <ReRenderOnMount>
        {!isEFGInternal ||
        (isEFGInternal && !optionData?.hasSingleOptionValue) ? (
          <div
            className="PDPMobile__PDPMobileConfigurationPhotoGallery-wrapper"
            ref={galleryRef}
          >
            <PDPMobileConfigurationPhotoGallery
              slug={slug}
              isEditOrder={isEditOrder}
              pdpIsModularBYOProduct={pdpIsModularBYOProduct}
              marxentPostHogFlag={mobileMarxentFeatureFlag}
              hideMarxent={hideMarxent}
              unavailableFabric={unavailableFabric}
            />
          </div>
        ) : (
          <div className="PDPMobile__PDPMobileConfigurationPhotoGallery-wrapper-filler" />
        )}
      </ReRenderOnMount>

      <FixedWidthContainer>
        <div key={efgInternalKey}>
          {showDiscontinuedNotification ? (
            <PDPAlertBar type="error">
              <PDPDiscontinuedMessage
                discontinuedCategoryMsg={discontinuedCategoryMsg}
              />
            </PDPAlertBar>
          ) : (
            <div className="PDPMobile__PDPAlertBar-filler" />
          )}

          {!isOutOfStock && !showDiscontinuedNotification ? (
            <div className="PDPMobile__PDPChooseYourOptions-wrapper">
              {mobileMarxentFeatureFlag !== 'test' && (
                <PDPChooseYourOptions
                  isEditOrder={isEditOrder}
                  setMarxentSelectedFabric={setMarxentSelectedFabric}
                />
              )}

              {pdpIsModularBYOProduct && mobileMarxentFeatureFlag !== 'test' ? (
                <SuspenseWithLoader>
                  <ModularAddProducts />
                </SuspenseWithLoader>
              ) : (
                <div />
              )}

              {pdpIsModularBYOProduct && mobileMarxentFeatureFlag === 'test' ? (
                <div className="border-t border-gray-light7">
                  <SuspenseWithLoader>
                    <ModularAddProductsMarxent
                      marxentSelectedFabric={marxentSelectedFabric}
                      unavailableFabric={unavailableFabric}
                      setHideMarxent={setHideMarxent}
                      setUnavailableFabric={setUnavailableFabric}
                      scrollToGallery={scrollToGallery}
                      key={slug}
                    />
                  </SuspenseWithLoader>
                </div>
              ) : null}

              {mobileMarxentFeatureFlag === 'test' && (
                <PDPChooseYourOptions
                  isEditOrder={isEditOrder}
                  setMarxentSelectedFabric={setMarxentSelectedFabric}
                />
              )}

              {!isEditOrder && <PDPUpSellProduct />}

              {!isEditOrder && <PDPProtectionPlanSelector />}
            </div>
          ) : (
            <div className="PDPMobile__PDPChooseYourOptions-wrapper-filler" />
          )}

          {isOutOfStock && !showDiscontinuedNotification ? (
            <PDPAlertsSection
              showOutOfStockNotification={showOutOfStockNotification}
            />
          ) : (
            <div className="PDPMobile__PDPAlertSection-filler" />
          )}

          {isEditOrder ? (
            <PDPCompleteOrder />
          ) : (
            <PDPAddToBagSection isOutOfStock={isOutOfStock} />
          )}
        </div>

        <PDPKeySellingPoints />

        <PDPSellingAttributes />

        {!isEditOrder && <PDPProductDescription />}

        {!pdpIsModularBYOProduct ? (
          <PDPDimensionsAndDetails />
        ) : (
          <PDPDetailsAndProductCareLink />
        )}

        {!isEditOrder && <PDPCustomerPhotos slug={slug} />}

        {!displayPressComp ? (
          <SuspenseWithLoader>
            <PDPCollectionSlider setDisplayPressComp={setDisplayPressComp} />
          </SuspenseWithLoader>
        ) : (
          <PDPPress />
        )}
      </FixedWidthContainer>
    </div>
  );
};

PDPMobileInner.propTypes = {
  discontinuedCategoryMsg: PropTypes.shape({
    category: PropTypes.string,
    link: PropTypes.string,
  }),
  pdpIsModularBYOProduct: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  showDiscontinuedNotification: PropTypes.bool,
  showOutOfStockNotification: PropTypes.shape({
    productId: PropTypes.number,
    show: PropTypes.bool,
  }),
  slug: PropTypes.string,
};

const PDPMobile = props => (
  <SuspenseWithLoader fallback={<PDPMobilePlaceholder />}>
    <PDPMobileInner {...props} />
  </SuspenseWithLoader>
);

export default PDPMobile;
